import type { Notification, NotificationPreference } from '@btsis/contracts';
import type { NotificationChannel, NotificationEvent, NotificationFrequency } from '@btsis/core';

export type LabelColor = 'default' | 'info' | 'success' | 'warning' | 'error';

/**
 * Where a delivery stands, as a colour. `suppressed` is deliberately not an
 * error: nobody failed, the recipient asked not to be told — packages/core's
 * `NOTIFICATION_SUPPRESSIONS`.
 */
export const NOTIFICATION_STATUS_COLOR: Record<Notification['status'], LabelColor> = {
  pending: 'default',
  sending: 'info',
  sent: 'success',
  failed: 'error',
  delivery_unknown: 'warning',
  suppressed: 'default',
};

/**
 * The catalogue key of an event: `receipt.issued` reads as `receiptIssued`, so
 * `notification.events.receiptIssued` and `notification.templates.receiptIssued`
 * resolve. The same fold as the server's `templateKeyOf`, and as `jobLabelKey`.
 */
export function notificationEventKey(event: string): string {
  return event.replace(/[^a-zA-Z0-9]+([a-zA-Z0-9])/g, (_whole, next: string) => next.toUpperCase());
}

const relativeFormatter = new Intl.RelativeTimeFormat('tr-TR', { numeric: 'auto' });

const DIVISIONS: readonly (readonly [number, Intl.RelativeTimeFormatUnit])[] = [
  [60, 'second'],
  [60, 'minute'],
  [24, 'hour'],
  [7, 'day'],
  [4.34524, 'week'],
  [12, 'month'],
  [Number.POSITIVE_INFINITY, 'year'],
];

/**
 * "3 dakika önce", in the Turkish locale. The bell shows when something
 * happened rather than a timestamp, because the whole list is minutes old.
 */
export function relativeTime(value: string, now: number = Date.now()): string {
  const at = new Date(value).getTime();
  if (Number.isNaN(at)) return '';
  let duration = (at - now) / 1000;
  for (const [amount, unit] of DIVISIONS) {
    if (Math.abs(duration) < amount) return relativeFormatter.format(Math.round(duration), unit);
    duration /= amount;
  }
  return relativeFormatter.format(Math.round(duration), 'year');
}

/**
 * What this person actually gets on this channel for this event, in the order
 * the server resolves it — the event's own choice, then the catch-all choice
 * that covers every event, then the event's shipped default. Mirrored exactly
 * from `preferencesOf` and `channelsFor` in packages/server and packages/core,
 * so the screen shows what the dispatcher would do rather than a guess at it.
 */
export function effectiveFrequency(
  chosen: readonly NotificationPreference[],
  event: NotificationEvent | null,
  channel: NotificationChannel,
  defaults: readonly NotificationChannel[],
): NotificationFrequency {
  if (event !== null) {
    const own = chosen.find((row) => row.event === event && row.channel === channel);
    if (own) return own.frequency;
  }
  const catchAll = chosen.find((row) => row.event === null && row.channel === channel);
  if (catchAll) return catchAll.frequency;
  return defaults.includes(channel) ? 'immediate' : 'off';
}

const RECORD_PATHS: Record<string, (id: string) => string> = {
  donation: (id) => `/donations/${id}`,
  pledge: () => '/pledges',
  receipt: () => '/receipts',
  campaignStaffAssignment: () => '/campaign-staff',
  user: () => '/users',
};

/**
 * Where the record a notification is about is read. A masked session is shown no
 * subject and no body, so the event's title and this link are what it can act on;
 * a record with no page of its own, or none this screen knows, has no link.
 */
export function notificationRecordPath(
  causeEntity: string | null,
  causeId: string | null,
): string | null {
  if (causeEntity === null || causeId === null) return null;
  return RECORD_PATHS[causeEntity]?.(causeId) ?? null;
}
